import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener datos del asistente de salud de Contentful
 * Incluye contenido para el asistente virtual de salud
 *
 * @returns {Object} Objeto con loading, error, datos procesados y funciones utilitarias
 */
export const useContentfulHealthAssistant = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'health-assistant'] as const,
		queryFn: queriesApi.getHealthAssistant,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Health Assistant desde Contentful:', error)
	}

	// Procesar datos del asistente de salud
	const beneficioCollection = useMemo(() => {
		if (!rawData || isLoading || error) return []

		try {
			return (
				rawData?.homeCollection?.items?.[0]?.vistas?.asistenteDeSalud?.beneficioCollection?.items ||
				[]
			)
		} catch (transformError) {
			console.error('Error al procesar datos del asistente de salud:', transformError)
			return []
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		beneficioCollection,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulHealthAssistant
